'use client';

import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/lib/motion';
import { Section } from '@/components/layout/Section';

interface Step {
  number: string;
  title: string;
  description: string;
}

interface HowItWorksProps {
  steps?: Step[];
  eyebrow?: string;
  heading?: string;
  description?: string;
}

const DEFAULT_STEPS: Step[] = [
  {
    number: '01',
    title: 'Affinez votre recherche',
    description:
      'Filtres avancés, carte interactive et alertes sur-mesure pour révéler les biens qui vous correspondent.',
  },
  {
    number: '02',
    title: 'Échangez avec nos experts',
    description:
      'Un conseiller dédié orchestre vos visites, vous éclaire sur les quartiers et négocie pour vous.',
  },
  {
    number: '03',
    title: 'Concrétisez en confiance',
    description:
      'Accompagnement notarial, financement, gestion locative — du compromis aux clefs en main.',
  },
];

export function HowItWorks({
  steps = DEFAULT_STEPS,
  eyebrow = 'Notre méthode',
  heading = 'Trois temps, une expérience d’exception',
  description,
}: HowItWorksProps) {
  return (
    <Section
      eyebrow={eyebrow}
      heading={heading}
      description={description}
      align="center"
      spacing="lg"
    >
      <motion.ol
        variants={staggerContainer(0.12, 0.1)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
        className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-12 relative"
      >
        {/* Continuous hairline behind numerals (desktop) */}
        <div
          className="hidden md:block absolute left-0 right-0 top-[42px] h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent"
          aria-hidden
        />

        {steps.map((step, i) => (
          <motion.li
            key={i}
            variants={fadeUp}
            className="relative flex flex-col items-center md:items-start text-center md:text-left"
          >
            <div className="relative mb-6">
              <span className="absolute inset-0 -m-3 rounded-full bg-surface-cream/70 backdrop-blur-sm" aria-hidden />
              <span className="relative font-display text-display-lg text-accent leading-none italic">
                {step.number}
              </span>
            </div>
            <h3 className="font-display text-h2 text-ink mb-3 leading-tight">{step.title}</h3>
            <p className="text-base text-ink-secondary leading-relaxed max-w-sm">
              {step.description}
            </p>
          </motion.li>
        ))}
      </motion.ol>
    </Section>
  );
}
